import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect } from 'react';
import { useAuth0 } from '@brighte/passwordless-authenticator';
import { Stack } from '@spark-web/stack';
import { VerticalStepper } from '@spark-web/vertical-stepper';
import PageTitle from '../../components/PageTitle/PageTitle';
import ApplicationUnderReviewStep from '../../components/Step/ApplicationUnderReviewStep';
import Step from '../../components/Step/Step';
import WithdrawnApplication from '../SubmittedApplication/WithdrawnApplication';
import CustomerPortalCard from './CustomerPortalCard';
import DeclinedApplication from './DeclinedApplication';
import { useGetNextStepIndex, useGetStatusSteps } from './hooks';
import { ApplicationSubmissionStatus, StatusStep, StepType } from './types';

const CUSTOMER_PORTAL_ELIGIBLE_STATUSES = new Set([
  ApplicationSubmissionStatus.SUBMITTED,
  ApplicationSubmissionStatus.REFERRED,
  ApplicationSubmissionStatus.APPROVED,
]);

type NextStepsProps = {
  readonly status: ApplicationSubmissionStatus;
};

function NextSteps({ status }: NextStepsProps) {
  const steps = useGetStatusSteps(status);
  const activeStep = useGetNextStepIndex(steps, status);
  const { clearCredentials } = useAuth0();
  const flags = useFlags();
  const showCustomerPortalAction =
    (flags['redirect-customer-to-customer-portal'] ?? false) && CUSTOMER_PORTAL_ELIGIBLE_STATUSES.has(status);

  useEffect(() => {
    clearCredentials();
  }, []);

  if (status === ApplicationSubmissionStatus.WITHDRAWN) {
    return <WithdrawnApplication />;
  }

  if (status === ApplicationSubmissionStatus.DECLINED) {
    return <DeclinedApplication />;
  }
  return (
    <>
      <PageTitle status={status} />
      <VerticalStepper activeIndex={activeStep}>
        {steps.map((step: StepType) =>
          step.key === StatusStep.APPLICATION_UNDER_REVIEW ? (
            <ApplicationUnderReviewStep key={step.key} />
          ) : (
            <Step {...step} key={step.key} />
          ),
        )}
      </VerticalStepper>
      {showCustomerPortalAction && (
        <Stack paddingTop="large">
          <CustomerPortalCard applicationStatus={status} />
        </Stack>
      )}
    </>
  );
}

export default NextSteps;
